import { Injectable } from '@jkhy/frontend-utils/di';
import {
    AchChildSupportPaymentsClient,
    AchFederalTaxPaymentsClient,
    AchPaymentModelDto,
    AchPaymentsClient,
    AchStateTaxPaymentsClient,
    ChildSupportPaymentModelDto,
    FederalTaxPaymentModelDto,
    StateTaxPaymentModelDto,
} from '@treasury/api/bo';
import { downloadBlob } from '../../../utilities/file-handling';
import {
    AchChildSupportPayment,
    AchFederalTaxPayment,
    AchPayment,
    AchStateTaxPayment,
} from '../../types/ach/';

type achDownloadType = 'pdf' | 'csv' | 'nacha';

@Injectable()
export class AchPaymentsService {
    constructor(
        private readonly achPaymentsClient: AchPaymentsClient,
        private readonly achChildSupportPaymentsClient: AchChildSupportPaymentsClient,
        private readonly achStateTaxPaymentsClient: AchStateTaxPaymentsClient,
        private readonly achFederalTaxPaymentsClient: AchFederalTaxPaymentsClient
    ) {}

    public async getRegularPaymentById(id: string) {
        const dto = (await this.achPaymentsClient.achPaymentGet(id)).data;
        if (dto.errorMessage) {
            return new Error(dto.errorMessage);
        }
        if (!dto.achPayment) {
            return new Error('No ACH Payment found');
        }
        return new AchPayment(dto.achPayment);
    }

    public async getChildSupportPaymentById(id: string) {
        const dto = (await this.achChildSupportPaymentsClient.achChildSupportPaymentGet(id)).data;
        if (dto.errorMessage) {
            return new Error(dto.errorMessage);
        }
        if (!dto.childSupportPayment) {
            return new Error('No ACH Child Support Payment found');
        }
        return new AchChildSupportPayment(
            dto.childSupportPayment as ChildSupportPaymentModelDto & AchPaymentModelDto
        );
    }

    public async getStateTaxPaymentById(id: string) {
        const dto = (await this.achStateTaxPaymentsClient.stateTaxPaymentGet(id)).data;
        if (dto.errorMessage) {
            return new Error(dto.errorMessage);
        }
        if (!dto.stateTaxPayment) {
            return new Error('No ACH State Tax Payment found');
        }
        return new AchStateTaxPayment(
            dto.stateTaxPayment as StateTaxPaymentModelDto & AchPaymentModelDto
        );
    }

    public async getFederalTaxPaymentById(id: string) {
        const dto = (await this.achFederalTaxPaymentsClient.federalTaxPaymentGet(id)).data;
        if (dto.errorMessage) {
            return new Error(dto.errorMessage);
        }
        if (!dto.federalTaxPayment) {
            return new Error('No ACH Federal Tax Payment found');
        }
        return new AchFederalTaxPayment(
            dto.federalTaxPayment as FederalTaxPaymentModelDto & AchPaymentModelDto
        );
    }

    public async downloadAchPaymentDetails(id: string, outputType: achDownloadType) {
        const response = await this.achPaymentsClient.achPaymentDownloadAchPayment(id, {
            outputType,
        });
        const { content, fileName, mimeType } = response.data;
        if (!content) {
            throw new Error('No file content');
        }

        const extension = outputType === 'nacha' ? 'txt' : outputType;
        const filename = `${fileName}`;

        if (outputType === 'pdf') {
            const linkSource = `data:application/pdf;base64,${content}`;
            const downloadLink = document.createElement('a');
            downloadLink.href = linkSource;
            downloadLink.download = fileName as string;
            downloadLink.click();
            return undefined;
        }

        const binaryString = window.atob(content);
        const blob = new Blob([binaryString], { type: mimeType });
        return downloadBlob(blob, filename, new Date().toISOString().split('T')[0], extension);
    }
}
